import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {
  ChatAddContactRequest,
  ChatContactView,
  ChatCurrentUserView,
  ChatMessageView, ChatSendMessageRequest,
  ChatUserSearchItem
} from '../../../models/generated-models';
import {catchError, Observable, switchMap} from 'rxjs';


@Injectable({providedIn: 'root'})
export class ChatRestService {
  private readonly http = inject(HttpClient);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly baseUrl = environment.serverUrl;

  me() {
    return this.http.get<ChatCurrentUserView>(`${this.baseUrl}/api/chat/me`);
  }

  updateAllowChat(enabled: boolean) {
    return this.http.post<ChatCurrentUserView>(`${this.baseUrl}/api/chat/me/allow-chat`, {
      enabled
    });
  }

  updateAllowSearch(enabled: boolean) {
    return this.http.post<ChatCurrentUserView>(`${this.baseUrl}/api/chat/me/allow-search`, {
      enabled
    });
  }

  searchUsers(query: string) {
    return this.http.get<ChatUserSearchItem[]>(`${this.baseUrl}/api/chat/users/search`, {
      params: {query}
    });
  }

  contacts() {
    return this.http.get<ChatContactView[]>(`${this.baseUrl}/api/chat/contacts`);
  }

  addContact(contactId: number) {
    const payload: ChatAddContactRequest = {contactId};
    return this.http.post<ChatContactView>(`${this.baseUrl}/api/chat/contacts`, payload);
  }

  /** Of the given user ids, return those the current user is allowed to start a chat with. */
  chatEnabledUserIds(userIds: number[]) {
    return this.http.post<number[]>(`${this.baseUrl}/api/chat/users/chat-enabled`, {userIds});
  }

  messages(contactId: number, options?: {limit?: number; beforeMessageId?: number | null; afterMessageId?: number | null}) {
    const query = this.buildMessageQuery(options);
    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap(token => this.loadMessagesViaWebSocket(contactId, token, query)),
      catchError(() => this.http.get<ChatMessageView[]>(`${this.baseUrl}/api/chat/messages/${contactId}`, {params: query}))
    );
  }

  sendMessage(request: ChatSendMessageRequest) {
    return this.http.post<ChatMessageView>(`${this.baseUrl}/api/chat/messages`, request);
  }

  sendAttachments(receiverId: number, files: File[]) {
    const formData = new FormData();
    formData.append('receiverId', String(receiverId));
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });
    return this.http.post<ChatMessageView[]>(`${this.baseUrl}/api/chat/messages/attachments`, formData);
  }

  unreadCount() {
    return this.http.get<number>(`${this.baseUrl}/api/chat/unread-count`);
  }

  private loadMessagesViaWebSocket(contactId: number, token: string, query: Record<string, string>): Observable<ChatMessageView[]> {
    return new Observable<ChatMessageView[]>((observer) => {
      const url = new URL(`${this.baseUrl}/ws/chat/messages/${contactId}`, window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      Object.entries(query).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
      if (token) {
        url.searchParams.set('access_token', token);
      }

      const socket = new WebSocket(url.toString());
      let settled = false;

      socket.onmessage = (event) => {
        try {
          const messages = JSON.parse(event.data) as ChatMessageView[];
          settled = true;
          observer.next(messages);
          observer.complete();
        } catch (error) {
          settled = true;
          observer.error(error);
        } finally {
          if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
            socket.close();
          }
        }
      };

      socket.onerror = () => {
        settled = true;
        observer.error(new Error('WebSocket message load failed'));
      };

      socket.onclose = (event) => {
        if (!settled && event.code !== 1000) {
          observer.error(new Error(`WebSocket closed with code ${event.code}`));
        }
      };

      return () => {
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close();
        }
      };
    });
  }

  private buildMessageQuery(options?: {limit?: number; beforeMessageId?: number | null; afterMessageId?: number | null}): Record<string, string> {
    const params: Record<string, string> = {};
    const limit = options?.limit ?? 30;
    if (limit) {
      params['limit'] = String(limit);
    }
    if (options?.beforeMessageId != null) {
      params['beforeMessageId'] = String(options.beforeMessageId);
    }
    if (options?.afterMessageId != null) {
      params['afterMessageId'] = String(options.afterMessageId);
    }
    return params;
  }
}
